import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Loader2 } from "lucide-react";
import { yen, dateJP } from "@/lib/format";
import { PAYMENT_METHOD } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useLookups } from "@/context/LookupContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const METHOD_LABEL = Object.fromEntries(PAYMENT_METHOD.map((m) => [m.value, m.label]));

export default function Payments() {
  const { hasWrite } = useAuth();
  const { nameOf } = useLookups();
  const canWrite = hasWrite("payments");
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ invoice_id: "", amount: "", paid_date: "", method: "bank_transfer", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, i] = await Promise.all([api.get("/billing/payments"), api.get("/billing/invoices")]);
      setPayments(p.data);
      setInvoices(i.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unpaidInvoices = invoices.filter((i) => (i.balance ?? (i.amount - i.paid_amount)) > 0);

  const onSelectInvoice = (id) => {
    const inv = invoices.find((x) => x.id === id);
    setForm((f) => ({ ...f, invoice_id: id, amount: inv ? (inv.balance ?? inv.amount - inv.paid_amount) : "" }));
  };

  const submit = async () => {
    if (!form.invoice_id) { toast.error("請求を選択してください"); return; }
    const inv = invoices.find((x) => x.id === form.invoice_id);
    setSaving(true);
    try {
      await api.post("/billing/payments", {
        invoice_id: form.invoice_id,
        property_id: inv?.property_id,
        tenant_id: inv?.tenant_id,
        amount: Number(form.amount),
        paid_date: form.paid_date,
        method: form.method,
        note: form.note,
      });
      toast.success("入金を登録しました");
      setOpen(false);
      setForm({ invoice_id: "", amount: "", paid_date: "", method: "bank_transfer", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "登録に失敗しました");
    } finally { setSaving(false); }
  };

  const columns = [
    { key: "paid_date", label: "入金日", render: (r) => dateJP(r.paid_date) },
    { key: "tenant_id", label: "入居者", render: (r) => nameOf("tenants", r.tenant_id) },
    { key: "property_id", label: "物件", render: (r) => nameOf("properties", r.property_id) },
    { key: "amount", label: "入金額", render: (r) => <span className="font-medium text-emerald-600">{yen(r.amount)}</span> },
    { key: "method", label: "入金方法", render: (r) => METHOD_LABEL[r.method] || r.method || "-" },
    { key: "note", label: "備考", render: (r) => <span className="text-muted-foreground">{r.note || "-"}</span> },
  ];

  const del = async (row) => {
    try { await api.delete(`/billing/payments/${row.id}`); toast.success("削除しました"); load(); }
    catch { toast.error("削除に失敗しました"); }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="入金管理"
        subtitle="家賃入金の記録"
        actions={canWrite && (
          <Button onClick={() => setOpen(true)} className="bg-brand hover:bg-brand-hover text-white" data-testid="add-payment">
            <Plus className="w-4 h-4 mr-1.5" />入金を記録
          </Button>
        )}
      />
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
      ) : (
        <DataTable columns={columns} rows={payments} canWrite={canWrite}
          onDelete={canWrite ? del : null} testid="payments" emptyText="入金記録がありません" />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>入金を記録</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">対象請求 *</Label>
              <select value={form.invoice_id} onChange={(e) => onSelectInvoice(e.target.value)}
                className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm" data-testid="payment-invoice">
                <option value="">未収の請求を選択</option>
                {unpaidInvoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.year_month}｜{nameOf("properties", i.property_id)}｜{nameOf("tenants", i.tenant_id)}｜残 {yen(i.balance ?? i.amount - i.paid_amount)}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">入金額 *</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">入金日</Label>
                <Input type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} data-testid="payment-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">入金方法</Label>
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
                className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm">
                {PAYMENT_METHOD.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">備考</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
            <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="payment-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}記録
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
